/*
* =======================================================================
* FILE: CaregiverObservationPage1.jsx
* -----------------------------------------------------------------------
* Displays the FIRST page of the caregiver's observation feedback.
* Updated with PDF-compatible selection styles (inline borders instead of rings/shadows)
* =======================================================================
*/
import React from 'react';
import YesIcon from '../../../../Assets/Prepratory/picsvg_download (17).svg';
import SometimesIcon from '../../../../Assets/Prepratory/picsvg_download (18).svg';
import NoIcon from '../../../../Assets/Prepratory/picsvg_download (19).svg';
import NotSureIcon from '../../../../Assets/Prepratory/picsvg_download (20).svg';

// --- Fixed pixel sizes for PDF compatibility ---
const ICON_SIZE = 56;
const ICON_CONTAINER_SIZE = 70;

// Use your own question array
const questions = [
    "My child finds the classroom and school a welcoming and safe space.",
    "My child participates in academic and other activities in school.",
    "My child finds the grade-level curriculum difficult.",
    "My child is making good progress as per their grade.",
    "My child is getting the support needed from school."
];

const responseIcons = [
    { img: YesIcon, alt: "Yes", color: "#5DB141" },
    { img: SometimesIcon, alt: "Sometimes", color: "#F7B42C" },
    { img: NoIcon, alt: "No", color: "#F2555B" },
    { img: NotSureIcon, alt: "Not sure", color: "#C9A145" },
];

const homeResources = [
    { key: 'Books and Magazines', icon: '📚' },
    { key: 'Newspapers', icon: '📰' },
    { key: 'Toys, Games and sports', icon: '🧸' },
    { key: 'Phone and Computer', icon: '💻' },
    { key: 'Internet', icon: '🌐' },
    { key: 'Public Broadcast System', icon: '📻' },
    { key: 'Resources for CWSN', icon: '♿' },
];


const responseKeys = [
    "finds_school_safe",          // Question 1
    "participates_in_activities", // Question 2
    "finds_curriculum_difficult", // Question 3
    "making_good_progress",       // Question 4
    "gets_school_support"         // Question 5
];

// Helper to get selection style - uses inline border for PDF compatibility
const getSelectionStyle = (isSelected) => {
    if (isSelected) {
        return {
            border: '3px solid #3B82F6',
            backgroundColor: '#DBEAFE',
            borderRadius: '50%',
            boxSizing: 'border-box'
        };
    }
    return {
        border: '2px solid transparent',
        borderRadius: '50%',
        boxSizing: 'border-box'
    };
};

// --- Page 1 Component ---

export const CaregiverObservationPage1 = ({ assignment }) => {
    if (!assignment || !assignment.caregiver_observation) {
        return <div className="report-page bg-white flex items-center justify-center font-[poppins]"><p>Caregiver
            Observation data not available.</p></div>;
    }

    const { resources = [], assessment = {}, other_resources = '' } = assignment.caregiver_observation;

    return (
        <div className="report-page bg-white font-[poppins]" style={{
            width: '210mm',
            height: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.5',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            <h1 className="text-3xl font-bold text-[#e36c09] text-center mb-2">Your Child Matters!</h1>

            <p className="text-[#e36e0c] text-lg mb-3">Tick the resources available to your child at home.</p>
            <div className="grid grid-cols-7 gap-4 mb-3">
                {homeResources.map(({ key, icon }) => (
                    <div key={key} className="flex flex-col items-center">
                        <div className="text-4xl mb-1">{icon}</div>
                        <span className="text-center text-xs font-semibold h-10">{key}</span>
                        <div
                            style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid black',
                                marginTop: '16px',
                                backgroundColor: resources.includes(key) ? 'black' : 'transparent'
                            }}
                        ></div>
                    </div>
                ))}
            </div>
            <div className="flex items-center w-full mt-4 mb-4">
                <span className="text-[15px] ml-2 mr-2" style={{ fontWeight: 400 }}>
                    Any other (please specify)
                </span>
                <span className="flex-1 border-b border-black" style={{ marginTop: 2 }}></span>
                {other_resources && (
                    <span className="ml-2 text-[15px] italic">{other_resources}</span>
                )}
            </div>


            <table className="w-full border border-[#e36c09] border-collapse bg-white">
                <thead>
                    <tr>
                        <th colSpan={2}
                            className="bg-[#e36c09] text-white text-center py-3 text-[18px] font-bold border-b border-[#e36c09]"
                        >
                            How can I know your child better?
                        </th>
                    </tr>
                    <tr>
                        <th colSpan={2}
                            className=" p-1 font-bold bg-[#fbd4b4] text-[14px] text-left text-black border-b border-[#e36c09]"
                        >
                            Circle the most appropriate option for each statement.
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {questions.map((text, i) => (
                        <tr key={i} className="border-b border-[#e36c09]">
                            <td className="bg-[#fde9d9] align-top px-3 py-2 w-[43%] border-r border-[#e36c09]">
                                <span className="text-[16px] font-normal">{i + 1}. {text}</span>
                            </td>
                            <td className=" align-top w-[57%] p-0">
                                {/* Icons row with highlight - using inline styles for PDF */}
                                <div className="flex flex-row w-full justify-between items-center pt-1 pb-1 px-1">
                                    {responseIcons.map(({ img, alt }, idx) => {
                                        const isSelected = assessment[responseKeys[i]] === alt;
                                        return (
                                            <div
                                                key={alt}
                                                style={{
                                                    width: ICON_CONTAINER_SIZE,
                                                    height: ICON_CONTAINER_SIZE,
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    margin: '0 auto',
                                                    padding: '4px',
                                                    ...getSelectionStyle(isSelected)
                                                }}
                                            >
                                                <img
                                                    src={img}
                                                    alt={alt}
                                                    style={{ width: ICON_SIZE, height: ICON_SIZE }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Option labels row remains unchanged */}
                                <div className="flex flex-row w-full">
                                    <div className="flex-1 text-center py-[11px] text-[14px]  text-white" style={{
                                        background: '#e36c09',
                                        borderRight: "1px solid #fff"
                                    }}>Yes
                                    </div>
                                    <div className="flex-1 text-center py-[11px] text-[14px]  text-white" style={{
                                        background: '#e36c09',
                                        borderRight: "1px solid #fff"
                                    }}>Sometimes
                                    </div>
                                    <div className="flex-1 text-center py-[11px] text-[14px]  text-white" style={{
                                        background: '#e36c09',
                                        borderRight: "1px solid #fff"
                                    }}>No
                                    </div>
                                    <div className="flex-1 text-center py-[11px] text-[14px]  text-white"
                                        style={{ background: '#e36c09' }}>Not sure
                                    </div>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
